import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { AlertTriangle } from 'lucide-react';
import { EnterpriseWorkspaceNav } from '../components/enterprise/EnterpriseWorkspaceNav';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useEnterpriseWorkspaceInterventions } from '../hooks/use-enterprise-workspace-interventions';
import { workspacePath } from '../lib/enterprise-work-routes';

export default function EnterpriseWorkspaceInterventions() {
  const location = useLocation();
  const { loading, error, scope, roleAction, responseSlaHours, slaBreaches, queue } = useEnterpriseWorkspaceInterventions(30);
  const [severityFilter, setSeverityFilter] = useState<'all' | 'critical' | 'high' | 'elevated'>('all');
  const [domainFilter, setDomainFilter] = useState<'all' | 'leadership' | 'friendship' | 'family' | 'romantic'>('all');

  const filtered = useMemo(
    () =>
      queue.filter((item) => {
        const severityPass = severityFilter === 'all' || item.severity === severityFilter;
        const domainPass = domainFilter === 'all' || item.relationshipDomain === domainFilter;
        return severityPass && domainPass;
      }),
    [queue, severityFilter, domainFilter],
  );

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#020617_0%,#0b1220_100%)]">
      <EnterpriseWorkspaceNav />
      <div className="mx-auto max-w-5xl space-y-4 px-4 py-6">
        <header className="rounded-xl border border-slate-700 bg-slate-900/80 p-5">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-300" />
            <h1 className="text-2xl font-bold text-slate-100">Interventions</h1>
          </div>
          {scope ? <p className="mt-2 text-xs text-blue-300">Queue scope: {scope}</p> : null}
          {typeof responseSlaHours === 'number' ? <p className="mt-1 text-xs text-slate-400">Response SLA: {responseSlaHours}h</p> : null}
          {typeof slaBreaches === 'number' ? <p className="mt-1 text-xs text-amber-300">SLA breaches in queue: {slaBreaches}</p> : null}
          {roleAction ? <p className="mt-2 text-sm text-slate-200">{roleAction}</p> : null}
        </header>

        <Card className="border-slate-700 bg-slate-900/80 p-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="space-y-1 text-sm text-slate-300">
              <span className="text-xs uppercase tracking-wide text-slate-400">Severity</span>
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value as 'all' | 'critical' | 'high' | 'elevated')}
                className="block rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-slate-100"
              >
                <option value="all">All severities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="elevated">Elevated</option>
              </select>
            </label>
            <label className="space-y-1 text-sm text-slate-300">
              <span className="text-xs uppercase tracking-wide text-slate-400">Domain</span>
              <select
                value={domainFilter}
                onChange={(e) => setDomainFilter(e.target.value as 'all' | 'leadership' | 'friendship' | 'family' | 'romantic')}
                className="block rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-slate-100"
              >
                <option value="all">All domains</option>
                <option value="leadership">Leadership</option>
                <option value="friendship">Friendship</option>
                <option value="family">Family</option>
                <option value="romantic">Romantic</option>
              </select>
            </label>
            <p className="text-sm text-slate-300">Showing {filtered.length} of {queue.length} flagged events</p>
          </div>
        </Card>

        {loading ? <p className="text-sm text-slate-300">Loading intervention queue…</p> : null}
        {error ? <p className="text-sm text-amber-300">{error}</p> : null}

        {!loading && !error && filtered.length === 0 ? (
          <Card className="border-slate-700 bg-slate-900/80 p-4 text-slate-300">
            No interventions match this filter right now.
          </Card>
        ) : null}

        {!loading && !error
          ? filtered.map((item) => (
              <Card key={item.id} className="border-slate-700 bg-slate-900/80 p-4">
                <p className="text-sm font-semibold text-slate-100">
                  {item.severity.toUpperCase()} · RTI {item.rtiScore} · {item.relationshipDomain}
                </p>
                <p className="mt-1 text-xs text-slate-400">{new Date(item.occurredAt).toLocaleString()}</p>
                {item.entityLabel ? <p className="mt-1 text-sm text-slate-300">Entity: {item.entityLabel}</p> : null}
                {item.primaryTrigger ? <p className="mt-1 text-sm text-slate-300">Primary trigger: {item.primaryTrigger}</p> : null}
                <p className="mt-2 text-sm text-slate-200">{item.recommendation}</p>
              </Card>
            ))
          : null}

        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" className="border-slate-600 bg-slate-900 text-slate-100">
            <Link to={workspacePath(location.pathname, 'insights')}>Open insights</Link>
          </Button>
          <Button asChild variant="outline" className="border-slate-600 bg-slate-900 text-slate-100">
            <Link to="/enterprise/admin">Open enterprise admin</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

